import { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import { formatDate } from '../i18n/utils';

const ABSENCE_BADGE = {
  KRANK_MIT_ATTEST: 'bg-danger',
  KRANK_OHNE_ATTEST: 'bg-warning text-dark',
  FZT: 'badge-fzt',
};

export default function AbsenceView() {
  const { user, hasPermission } = useAuth();
  const { t, lang } = useI18n();
  const showDate = (v) => (lang === 'de' ? v : formatDate(v, lang));
  const canManage = hasPermission('manage_absences');

  const [absences, setAbsences] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const currentYear = new Date().getFullYear();
  const [filterYear, setFilterYear] = useState(currentYear);

  const emptyForm = {
    user_id: canManage ? '' : user?.id ?? '',
    start_date: '',
    end_date: '',
    absence_type: canManage ? 'KRANK_MIT_ATTEST' : 'FZT',
    notes: '',
  };
  const [form, setForm] = useState(emptyForm);
  const [compTimeEnabled, setCompTimeEnabled] = useState(false);
  useEffect(() => {
    api.get('/company-settings/').then((r) => setCompTimeEnabled(r.data.comp_time_enabled)).catch(() => {});
  }, []);
  const canAdd = canManage || compTimeEnabled;

  const load = async () => {
    setLoading(true);
    try {
      const calls = [
        canManage
          ? api.get(`/absences/all?year=${filterYear}`)
          : api.get(`/absences/?year=${filterYear}`),
      ];
      if (canManage) calls.push(api.get('/users/'));
      const results = await Promise.all(calls);
      setAbsences(results[0].data);
      if (canManage && results[1]) setUsers(results[1].data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filterYear]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await api.post('/absences/', {
        ...form,
        user_id: parseInt(form.user_id, 10),
      });
      setShowForm(false);
      setForm(emptyForm);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || t('absence.error.save'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('absence.confirm.delete'))) return;
    await api.delete(`/absences/${id}`);
    await load();
  };

  const userName = (uid) => {
    const u = users.find((u) => u.id === uid);
    return u ? `${u.first_name} ${u.last_name} (${u.personnel_number})` : `#${uid}`;
  };

  if (loading) return <div className="text-center"><div className="spinner-border" /></div>;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <h4 className="mb-0">
          <i className="bi bi-bandaid me-2"           />{t('absence.title')}
        </h4>
        <div className="d-flex gap-2 align-items-center">
          <select
            className="form-select form-select-sm"
            style={{ width: 100 }}
            value={filterYear}
            onChange={(e) => setFilterYear(parseInt(e.target.value, 10))}
          >
            {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          {canAdd && (
            <button className="btn btn-primary btn-sm" onClick={() => setShowForm(!showForm)}>
              <i className="bi bi-plus-circle me-2" />{t('absence.add')}
            </button>
          )}
        </div>
      </div>

      {showForm && (
        <div className="card mb-4">
          <div className="card-body">
            <h6 className="card-title">{t('absence.form.title')}</h6>
            {error && <div className="alert alert-danger py-2">{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                {canManage && (
                  <div className="col-md-6">
                    <label className="form-label">{t('absence.form.employee')}</label>
                    <select
                      className="form-select"
                      required
                      value={form.user_id}
                      onChange={(e) => setForm({ ...form, user_id: e.target.value })}
                    >
                      <option value="">{t('absence.form.choose')}</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.first_name} {u.last_name} ({u.personnel_number})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="col-md-6">
                  <label className="form-label">{t('absence.form.type')}</label>
                  <select
                    className="form-select"
                    value={form.absence_type}
                    onChange={(e) => setForm({ ...form, absence_type: e.target.value })}
                  >
                    {canManage && <option value="KRANK_MIT_ATTEST">{t('absence.type.KRANK_MIT_ATTEST')}</option>}
                    {canManage && <option value="KRANK_OHNE_ATTEST">{t('absence.type.KRANK_OHNE_ATTEST')}</option>}
                    {compTimeEnabled && <option value="FZT">{t('absence.type.FZT')}</option>}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">{t('absence.form.from')}</label>
                  <input
                    type="date"
                    className="form-control"
                    required
                    value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">{t('absence.form.to')}</label>
                  <input
                    type="date"
                    className="form-control"
                    required
                    value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">{t('absence.form.notes')}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  />
                </div>
              </div>
              <div className="mt-3 d-flex gap-2">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? t('absence.form.saving') : t('absence.form.save')}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => { setShowForm(false); setError(''); }}
                >
                  {t('absence.form.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {absences.length === 0 ? (
        <div className="text-muted text-center py-5">
          <i className="bi bi-calendar-x fs-1 d-block mb-2" />
          {t('absence.empty', { year: filterYear })}
        </div>
      ) : (
        <div className="card">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  {canManage && <th>{t('absence.col.employee')}</th>}
                  <th>{t('absence.col.type')}</th>
                  <th>{t('absence.col.from')}</th>
                  <th>{t('absence.col.to')}</th>
                  <th>{t('absence.col.workdays')}</th>
                  <th>{t('absence.col.notes')}</th>
                  <th>{t('absence.col.createdBy')}</th>
                  {canManage && <th></th>}
                </tr>
              </thead>
              <tbody>
                {absences.map((a) => (
                  <tr key={a.id}>
                    {canManage && <td>{userName(a.user_id)}</td>}
                    <td>
                      <span className={`badge ${ABSENCE_BADGE[a.absence_type]}`}>
                        {t(`absence.type.${a.absence_type}`)}
                      </span>
                    </td>
                    <td>{showDate(a.start_date)}</td>
                    <td>{showDate(a.end_date)}</td>
                    <td>{a.days_count}</td>
                    <td>{a.notes || '–'}</td>
                    <td>
                      {a.created_by_id
                        ? (canManage ? userName(a.created_by_id) : t('absence.admin'))
                        : '–'}
                    </td>
                    {canManage && (
                      <td>
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => handleDelete(a.id)}
                          title={t('absence.delete')}
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
